import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Formats a Unix timestamp into a human-readable time format (pt-BR).
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='HH:mm'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'HH:mm') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: ptBR });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year (pt-BR).
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='dd/MM/yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'dd/MM/yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat, { locale: ptBR });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string in pt-BR (e.g., há 3 horas).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true, locale: ptBR });
};

/**
 * Formats a Unix timestamp into a specified date format (pt-BR).
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='dd/MM/yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'dd/MM/yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: ptBR });
};

/**
 * Converts a detailed time description (pt-BR) into a shorter format, optionally appending 'atrás'.
 * @param {string} time - Detailed time description (e.g., 'há 1 minuto').
 * @param {boolean} [withAgo=false] - Whether to append 'atrás' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a pt-BR time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "atrás" to the end of the time string
  const suffix = withAgo ? ' atrás' : '';
  const timeMappings = {
    'há menos de um minuto': 'agora',
    'menos de um minuto atrás': 'agora',
    'há 1 minuto': `1m${suffix}`,
    'há cerca de 1 hora': `1h${suffix}`,
    'há 1 dia': `1d${suffix}`,
    'há cerca de 1 mês': `1mo${suffix}`,
    'há cerca de 1 ano': `1y${suffix}`,
    // Fallback for English strings (backwards compatibility)
    'less than a minute ago': 'agora',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/há |cerca de |mais de |quase |about |over |almost /g, '')
    .replace(/ minutos?/, 'm')
    .replace(/ horas?/, 'h')
    .replace(/ dias?/, 'd')
    .replace(/ meses|mês/, 'mo')
    .replace(/ anos?/, 'y')
    .replace(/ minutes? ago/, `m${suffix}`)
    .replace(/ hours? ago/, `h${suffix}`)
    .replace(/ days? ago/, `d${suffix}`)
    .replace(/ months? ago/, `mo${suffix}`)
    .replace(/ years? ago/, `y${suffix}`)
    .trim();
  return convertToShortTime || time;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
